import Plot from "react-plotly.js";
// import BSTextInput from "../forms/BSTextInput";
import styles from "./VolatilityAnalysis.module.css";
import useMounted from "../../hooks/useMounted";
// import { partial } from "lodash-es";
import { selectAuth } from "../auth/authSlice";
import {
  selectVolatilityAnalysis,
  // setVolatilityAnalysisData,
  VolatilityAnalysisData,
  Status,
  FuturesSelections,
  OptionsSelections,
  changeSelectedFutures,
  changeSelectedOptions,
  toggleSelectAllFutures,
  toggleSelectAllOptions,
  selectHasData,
  selectIsInitialized,
  selectIsEmpty,
} from "./volatilityAnalysisSlice";
import { useAppSelector, useAppDispatch } from "../../hooks/redux";
import { useNavigate, Link } from "react-router-dom";
import React, {
  useState,
  useEffect,
  ChangeEvent,
  FormEvent,
  useRef,
  SetStateAction,
  useMemo,
} from "react";
import { requestSymbol } from "./volatilityAnalysisActions";
import { Height } from "@mui/icons-material";
import { Annotations, PlotData, PlotRelayoutEvent } from "plotly.js";
import { Layout } from "plotly.js";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

// class FlatPrice {
//   #dates: Date[];
//   #values: number[];

//   constructor(dates: string[] | Date[], values: number[]) {
//     this.dates = dates;
//     this.values = values;
//   }
// }

export function VolatilityAnalysis(props: {}) {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  // "inputs" - what we watch that determines whether we update
  const { email, isLoggedIn, loginMessage } = useAppSelector(selectAuth);
  const { status } = useAppSelector(selectVolatilityAnalysis);
  const hasData = useAppSelector(selectHasData);
  const isInitialized = useAppSelector(selectIsInitialized);
  const isEmpty = useAppSelector(selectIsEmpty);
  // console.log("!!! status", status, "hasData", hasData, "isInitialized", isInitialized, "isEmpty", isEmpty);
  const volatilityAnalysisData: VolatilityAnalysisData = useAppSelector(
    (state) => state.volatilityAnalysis.data,
  );
  const portalFuturesData: { [symbol: string]: { expiration_date: string, name: string, first_notice_date: string, historical_data: { [date: string]: { open: number, high: number, low: number, close: number } } } } = useAppSelector(
    (state) => state.volatilityAnalysis.portalFuturesData,
  );

  const selectedFutures: FuturesSelections = useAppSelector(
    (state) => state.volatilityAnalysis.selectedFutures,
  );
  const selectedOptions: OptionsSelections = useAppSelector(
    (state) => state.volatilityAnalysis.selectedOptions,
  );

  const symbolRef = useRef<HTMLInputElement>(null);
  const firstSelectedFuturesRef = useRef<HTMLDivElement>(null);

  const [currentDate, setCurrentDate] = useState("");
  const [formSymbol, setFormSymbol] = useState("");
  const [symbolTouched, setSymbolTouched] = useState(false);
  const [symbolError, setSymbolError] = useState("");
  const [formSlider, setFormSlider] = useState<number>(0);
  const [generalError, setGeneralError] = useState("");
  const [submitDisabled, setSubmitDisabled] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [animationSpeed, setAnimationSpeed] = useState(1);
  const [timerIntervalId, setTimerIntervalId] = useState<
    number | undefined
  >(undefined);
  const [animationStep, setAnimationStep] = useState(0);
  const [nextAnimationStep, setNextAnimationStep] = useState(0);

  const [layout, setLayout] = useState<Partial<Layout>>({ title: "Option IVolatility Curve" });
  const [ivolUiRev, setIvolUiRev] = useState<string>("");

  const [expandedFutures, setExpandedFutures] = useState<Array<string>>([]);

  const isMounted = useMounted();

  const [startDate, setStartDate] = useState<Date>(
    new Date(new Date().setDate(new Date().getDate() - 10)),
  );
  const [endDate, setEndDate] = useState<Date>(new Date());

  const toggleExpandedFuture = (symbol: string) => {
    setExpandedFutures((prev) =>
      prev.includes(symbol)
        ? prev.filter((s) => s !== symbol)
        : [...prev, symbol],
    );
  };

  const resetState = () => {
    console.debug("resetState...");

    setCurrentDate("");
    setFormSymbol("");
    setSymbolTouched(false);
    setSymbolError("");
    setFormSlider(0);
    setGeneralError("");
    setSubmitDisabled(false);
    setIsLoading(false);
    setIsAnimating(false);
    setAnimationSpeed(1);
    setTimerIntervalId(undefined);
    setAnimationStep(0);
    setNextAnimationStep(0);
  };

  // Reset on navigate
  useEffect(() => {
    console.debug("resetting state...");
    resetState();
  }, [navigate]);

  // focus symbol field when component mounts
  useEffect(() => {
    console.debug("focusing symbol...");
    symbolRef.current?.focus();
  }, []); // <-- no dependecies so this only runs once after mount

  useEffect(() => {
    console.debug(`updated volatilityAnalysisData:`);
    console.debug(volatilityAnalysisData);
    const earliestDate = Object.keys(volatilityAnalysisData)[0];
    setCurrentDate(earliestDate);

    // console.debug("useEffect 2");
    // TODO: update this effect hook to handle all futures selections
    // (make need a sort-selection handler that depends on metadata)
  }, [volatilityAnalysisData]);

  // Scroll to first selected checkbox when data is rendered
  useEffect(() => {
    if (firstSelectedFuturesRef.current && hasData && !isLoading) {
      // Use setTimeout to ensure DOM has updated
      setTimeout(() => {
        firstSelectedFuturesRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }, 100);
    }
  }, [selectedFutures, hasData, isLoading]);

  useEffect(() => {
    console.debug("updating status...");
    console.debug(`status: ${status}`);
    switch (status) {
      case Status.IDLE:
        setSubmitDisabled(false);
        setIsLoading(false);
        break;
      case Status.LOADING:
        setSubmitDisabled(true);
        setIsLoading(true);
        break;
      default:
        setSubmitDisabled(false);
        setIsLoading(false);
    }
  }, [status, dispatch]);

  useEffect(() => {
    // clear previous timer, if it exists
    try {
      console.debug("destroying timer...");
      clearInterval(timerIntervalId);
    } catch {
      console.debug("Unable to destroy timer");
    }
    if (isAnimating) {
      if (animationStep >= nextAnimationStep) {
        setTimerIntervalId(setInterval(animate, 50 * animationSpeed));
      }
      setNextAnimationStep(animationStep + 1);
    }
  }, [animationSpeed, isAnimating, animationStep]);

  // Keyboard listener for arrow keys to control date slider
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Don't handle arrow keys if user is typing in an input field
      const target = e.target as HTMLElement;
      if (
        target.tagName === "INPUT" ||
        target.tagName === "TEXTAREA" ||
        target.tagName === "SELECT" ||
        target.isContentEditable
      ) {
        return;
      }

      // Only handle if we have data available
      if (!volatilityAnalysisData || Object.keys(volatilityAnalysisData).length === 0) {
        return;
      }

      const maxSliderValue = Object.keys(volatilityAnalysisData).length - 1;

      if (e.key === "ArrowLeft" || e.key === "ArrowRight") {
        e.preventDefault();

        if (e.key === "ArrowLeft" && formSlider > 0) {
          const newSliderValue = formSlider - 1;
          setFormSlider(newSliderValue);
          const dates = Object.keys(volatilityAnalysisData);
          setCurrentDate(dates[newSliderValue]);
        } else if (e.key === "ArrowRight" && formSlider < maxSliderValue) {
          const newSliderValue = formSlider + 1;
          setFormSlider(newSliderValue);
          const dates = Object.keys(volatilityAnalysisData);
          setCurrentDate(dates[newSliderValue]);
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [formSlider, volatilityAnalysisData]);

  const animate = () => {
    console.debug("animating...");
    console.debug(`${Object.keys(volatilityAnalysisData).length - 1}`);
    console.debug(`${formSlider}`);
    if (formSlider < Object.keys(volatilityAnalysisData).length - 1) {
      setFormSlider(formSlider + 1);
      setCurrentDate(Object.keys(volatilityAnalysisData)[formSlider + 1]);
      setAnimationStep(animationStep + 1);
    }
  };
  // disable submit button unless symbol is valid(?)
  // useEffect(() => {
  // setSubmitDisabled(!(emailValid && passwordValid));
  // }, [emailValid, passwordValid]);

  const calcSymbolMonthYear = (symbol: string) => {
    const month_letter = symbol.slice(-3, -2).toUpperCase();
    const year_2digit = symbol.slice(-2)
    const year_4digit = parseInt(year_2digit) + 2000;
    const month_letter_to_number: Record<string, number> = {
      'F': 1,
      'G': 2,
      'H': 3,
      'J': 4,
      'K': 5,
      'M': 6,
      'N': 7,
      'Q': 8,
      'U': 9,
      'V': 10,
      'X': 11,
      'Z': 12,
    }
    const month_number = month_letter_to_number[month_letter];
    return { month_number, year_4digit };
  }
  const sortByExpirationDateString = (a: string | null, b: string | null) => {
    //mirrors sort in fut list

    const dateA = a ? new Date(a) : null;
    const dateB = b ? new Date(b) : null;

    // Handle sorting logic with nulls
    if (dateA === null && dateB === null) return 0;
    if (dateA === null) return 1;
    if (dateB === null) return -1;

    return dateA.getTime() - dateB.getTime();
  };

  const handleFutCheckboxChange = (key: string) => {
    dispatch(changeSelectedFutures(key));
  };

  const handleOptCheckboxChange = (key: string) => {
    dispatch(changeSelectedOptions(key));
  };

  const handleFutSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(toggleSelectAllFutures());
  };

  const handleOptSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(toggleSelectAllOptions());
  };

  const createOnChange = (_fn: (s: string) => void) => {
    const onChange = (e: ChangeEvent<HTMLInputElement>) => {
      // Update credentials in state
      _fn(e.target.value);
    };
    return onChange;
  };

  const onSubmit = async (e: any) => {
    e.preventDefault();

    // setSubmitDisabled(true);
    // setIsLoading(true);
    setGeneralError("");

    if (formSymbol.length === 0) {
      setGeneralError("Please enter a symbol");
      return;
    }

    setIvolUiRev(Math.random().toString());

    dispatch(
      requestSymbol({
        symbol: formSymbol,
        startDate: startDate,
        endDate: endDate,
      }),
    );

    // setGeneralError(dataResult.user_message ? dataResult.user_message : "");

    // } else {
    // setErrorMessages("Please check your credentials!")
    // }
  };

  const updateSlider = (e: ChangeEvent<HTMLInputElement>) => {
    // e.preventDefault();
    setFormSlider(Number(e.target.value));
    setCurrentDate(Object.keys(volatilityAnalysisData)[Number(e.target.value)]);
  };

  const toggleAnimation = (e: ChangeEvent<HTMLInputElement>) => {
    // e.preventDefault();
    if (isAnimating) {
      // setAnimationStep(0);
      // setNextAnimationStep(0);
    }
    setIsAnimating(!isAnimating);
  };

  const symbolSort = (a: string, b: string): number => {
    if (a.slice(-2) < b.slice(-2)) {
      return -1;
    }
    if (a.slice(-2) > b.slice(-2)) {
      return 1;
    }
    if (a.slice(-2) === b.slice(-2)) {
      if (a.slice(-3) < b.slice(-3)) {
        return -1;
      }
      if (a.slice(-3) > b.slice(-3)) {
        return 1;
      }
      if (a.slice(-3) === b.slice(-3)) {
        return 0;
      }
    }
    return 999; // error? typescript will complain if we don't return a number
  };

  const handleRelayout = (newLayout: Partial<Layout>) => {
    setLayout({ ...layout, ...newLayout });
  };

  const ivolForwardCurvePlotData = useMemo(() => {
    if (!currentDate || !portalFuturesData || !volatilityAnalysisData) return { plotData: [], annotations: [] };

    const sortedSymbols = Object.keys(portalFuturesData).sort((a, b) => symbolSort(a, b)).filter(symbol => portalFuturesData[symbol].historical_data[currentDate]?.close ?? null !== null);
    const data = volatilityAnalysisData[currentDate];
    const futures = Object.keys(data);

    const ivol_calls = futures.map((future) => {
      const close = data[future].close;
      const ivolEntries = Object.entries(data[future].options[future]?.ivol?.Calls ?? {})
        .filter(([strike]) => Number(strike) <= close);
      if (!ivolEntries.length) return null;
      const [strike, ivol] = ivolEntries.reduce((a, b) =>
        Number(a[0]) > Number(b[0]) ? a : b
      );
      return { future, strike: Number(strike), ivol };
    });

    const ivol_puts = futures.map((future) => {
      const close = data[future].close;
      const ivolEntries = Object.entries(data[future].options[future]?.ivol?.Puts ?? {})
        .filter(([strike]) => Number(strike) >= close);
      if (!ivolEntries.length) return null;
      const [strike, ivol] = ivolEntries.reduce((a, b) =>
        Number(a[0]) < Number(b[0]) ? a : b
      );
      return { future, strike: Number(strike), ivol };
    });

    // Optimization: Avoid .find() in each iteration by building symbol->ivol and symbol->strike maps beforehand
    const callIvolMap = new Map(ivol_calls.filter(Boolean).map(ivol => [ivol!.future, ivol!.ivol]));
    const putIvolMap = new Map(ivol_puts.filter(Boolean).map(ivol => [ivol!.future, ivol!.ivol]));
    const callStrikeMap = new Map(ivol_calls.filter(Boolean).map(ivol => [ivol!.future, ivol!.strike]));
    const putStrikeMap = new Map(ivol_puts.filter(Boolean).map(ivol => [ivol!.future, ivol!.strike]));

    const yValues = sortedSymbols.map(symbol => {
      const call_ivol = callIvolMap.get(symbol);
      const put_ivol = putIvolMap.get(symbol);
      const call_strike = callStrikeMap.get(symbol);
      const put_strike = putStrikeMap.get(symbol);

      if (call_ivol != null && put_ivol != null) {
        const mean_ivol = (call_ivol + put_ivol) / 2;
        const max_ivol = Math.max(call_ivol, put_ivol);
        const min_ivol = Math.min(call_ivol, put_ivol);
        return { mean_ivol, max_ivol, min_ivol, put_strike, put_ivol, call_strike, call_ivol };
      } else if (call_ivol != null) {
        return { mean_ivol: call_ivol, max_ivol: call_ivol, min_ivol: call_ivol, put_strike: undefined, put_ivol: undefined, call_strike, call_ivol };
      } else if (put_ivol != null) {
        return { mean_ivol: put_ivol, max_ivol: put_ivol, min_ivol: put_ivol, put_strike, put_ivol, call_strike: undefined, call_ivol: undefined };
      } else {
        return null;
      }
    });

    const hoverTexts = yValues.map(y => {
      if (!y) return "";
      const putLine = (y.put_strike != null && y.put_ivol != null) ? `Put: ${y.put_strike} : ${(y.put_ivol * 100).toFixed(3)}%` : "";
      const callLine = (y.call_strike != null && y.call_ivol != null) ? `Call: ${y.call_strike} : ${(y.call_ivol * 100).toFixed(3)}%` : "";
      return [putLine, callLine].filter(Boolean).join("<br>");
    });

    const plotData = [
      {
        x: sortedSymbols,
        y: yValues.map(y => y?.mean_ivol ?? null),
        text: hoverTexts,
        hovertemplate: "%{x}<br>%{text}<extra></extra>",
        type: "scattergl",
        mode: "lines+markers",
        error_y: {
          type: "data",
          symmetric: false,
          array: yValues.map(y => (y?.max_ivol ?? 0) - (y?.mean_ivol ?? 0)),
          arrayminus: yValues.map(y => (y?.mean_ivol ?? 0) - (y?.min_ivol ?? 0)),
          visible: true,
        },
        marker: { color: "blue" },
        name: "IVolatility Forward Curve",
        connectgaps: true,
      }
    ] as PlotData[];

    // Annotations for x-axis: grey out symbols where ivol is null
    const annotations = sortedSymbols.map((symbol, i) => ({
      x: i,
      y: 0.02,
      yref: "paper" as const,
      xref: "x" as const,
      text: symbol,
      showarrow: false,
      font: { color: yValues[i] == null ? "#999999" : "#000000", size: 12 },
      textangle: 90,
      yanchor: "top" as const,
    })) as unknown as Annotations[];

    return { plotData, annotations };
  }, [volatilityAnalysisData, currentDate, portalFuturesData])

  const ivolCurveChartRange = useMemo(() => {
    if (!portalFuturesData || !volatilityAnalysisData) return [0, 0];

    let min = Infinity;
    let max = -Infinity;

    for (const date of Object.keys(volatilityAnalysisData)) {
      for (const future of Object.keys(volatilityAnalysisData[date]).filter((future) => Object.keys(portalFuturesData).includes(future))) {
        const types: ('Calls' | 'Puts')[] = ['Calls', 'Puts'];
        for (const type of types) {
          const ivolEntries = Object.entries(volatilityAnalysisData[date][future].options[future]?.ivol?.[type] ?? {})
            .filter(([strike]) => Number(strike) <= volatilityAnalysisData[date][future].close);
          if (!ivolEntries.length) continue;
          const [, ivol] = ivolEntries.reduce<[string, number]>(
            (a, b) => (Number(a[0]) > Number(b[0]) ? a : b),
            ivolEntries[0] as [string, number]
          );
          min = Math.min(min, ivol);
          max = Math.max(max, ivol);
        }
      }
    }

    return [min - (0.05 * min), max + (0.05 * max)];
  }, [volatilityAnalysisData, portalFuturesData])

  const forwardCurvePlotData = useMemo(() => {
    const sortedSymbols = Object.keys(portalFuturesData).sort((a, b) => symbolSort(a, b)).filter(symbol => portalFuturesData[symbol].historical_data[currentDate]?.close ?? null !== null);
    return [
      {
        x: sortedSymbols,
        y: sortedSymbols.map(symbol => portalFuturesData[symbol].historical_data[currentDate]?.close ?? null),
        type: "scattergl",
        mode: "lines+markers",
        marker: { color: "blue" },
        name: "Forward Curve",
        connectgaps: true,
      }
    ] as PlotData[]

  }, [portalFuturesData, currentDate])


  const continuousFlatPricePlotData = useMemo(() => {
    const sortedSymbols = Object.keys(portalFuturesData).sort((a, b) => symbolSort(a, b));
    const continuousFlatPriceData: { [date: string]: { open: number, high: number, low: number, close: number, symbol: string } } = {};
    let lastFirstNoticeDate: string = "";
    for (const symbol of sortedSymbols) {
      const firstNoticeDate = portalFuturesData[symbol].first_notice_date;
      const filteredDates = Object.keys(portalFuturesData[symbol].historical_data).filter((date: string) => date <= firstNoticeDate && date >= lastFirstNoticeDate);
      lastFirstNoticeDate = firstNoticeDate;
      for (const date of filteredDates) {
        continuousFlatPriceData[date] = { ...portalFuturesData[symbol].historical_data[date], symbol: symbol };
      }
    }
    return [
      {
        x: Object.keys(continuousFlatPriceData).sort(sortByExpirationDateString).map(date => new Date(date)),
        y: Object.keys(continuousFlatPriceData).sort(sortByExpirationDateString).map(date => continuousFlatPriceData[date].close),
        text: Object.keys(continuousFlatPriceData).sort(sortByExpirationDateString).map(date => continuousFlatPriceData[date].symbol),
        type: "scattergl",
        mode: "lines",
        line: { color: "lightblue" },
        name: "Continuous Flat Price",
        connectgaps: true,
        hoverinfo: "x+y+text",
      }
    ] as PlotData[]
  }, [portalFuturesData])

  const ivolChartRange = useMemo(() => {

    const days = Object.keys(volatilityAnalysisData);

    const min = Math.min(...days.flatMap(day => Object.keys(volatilityAnalysisData[day]).flatMap(future => Object.entries(volatilityAnalysisData[day][future].options).filter(([option, meta]) => selectedOptions[option]?.checked).flatMap(([option, value]) => Math.min(value.meta.deltaStrikeRange.Calls, value.meta.deltaStrikeRange.Puts)))));
    const max = Math.max(...days.flatMap(day => Object.keys(volatilityAnalysisData[day]).flatMap(future => Object.entries(volatilityAnalysisData[day][future].options).filter(([option, meta]) => selectedOptions[option]?.checked).flatMap(([option, value]) => Math.max(value.meta.deltaStrikeRange.Calls, value.meta.deltaStrikeRange.Puts)))));

    return [min, max];

  }, [volatilityAnalysisData, selectedOptions])

  const optionDescriptionFilters = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "EOM", "Short Dated"];
  const [selectedOptionDescriptionFilters, setSelectedOptionDescriptionFilters] = useState<string[]>([]);


  return (
    <div className="w-100 h-100" style={{ display: "flex", flexDirection: "column", minHeight: 0 }}>
      {/* Main content */}
      <div className="w-100 bg-light  border-bottom border-3 border-dark-subtle" style={{ flexShrink: 0 }}>
        <div className="d-flex flex-column justify-content-between bg-light w-50 mx-2 my-2 ms-4">
          <div className="d-flex flex-row align-items-end mb-2 gap-1">
            <div className="d-flex flex-column">
              <label className="mb-2"> Symbol:</label>
              <input
                type="text"
                className="form-control w-auto"
                id="inputSymbol"
                ref={symbolRef}
                value={formSymbol}
                placeholder="ES"
                onChange={createOnChange(setFormSymbol)}
                onBlur={() => setSymbolTouched(true)}
                onKeyDown={(e) => {
                  e.key == "Enter" && onSubmit(e);
                }}
              />
              {symbolTouched && symbolError && (
                <p className="text-danger validation-error">
                  {symbolError}
                </p>
              )}
            </div>
            <div className="d-flex flex-column ms-3">
              <label className="mb-2"> Option Start Date:</label>
              <DatePicker
                className="form-control w-auto"
                selected={startDate}
                onChange={(date: Date | null) => date && setStartDate(date)}
              />{" "}
            </div>
            <div className="d-flex flex-column">
              <label className="mb-2"> Option End Date:</label>
              <DatePicker
                className="form-control w-auto "
                selected={endDate}
                onChange={(date: Date | null) => date && setEndDate(date)}
              />{" "}
            </div>
            <div>
              <button
                className="btn btn-primary mb-0 mx-2 d-flex align-items-center gap-2"
                disabled={submitDisabled || isLoading}
                type="button"
                onClick={onSubmit}
              >
                {isLoading ? (
                  <>
                    <span
                      className="spinner-border spinner-border-sm"
                      role="status"
                      aria-hidden="true"
                    ></span>
                    <span> Loading... </span>
                  </>
                ) : (
                  <>Submit</>
                )}
              </button>
            </div>
          </div>
          <div className="d-flex flex-row">
            <div>
              {generalError && generalError !== "" && (
                <div className="text-danger text-center m-2">
                  {/* Error message */}
                  <p>{generalError}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Container for main content and footer with loading overlay */}
      <div className="flex-grow-1 d-flex flex-column position-relative" style={{ minHeight: 0 }}>
        {/* Loading overlay - covers main content and footer */}
        <div
          className="position-absolute d-flex justify-content-center align-items-center"
          style={{
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(255, 255, 255, 0.95)",
            zIndex: 9999,
            backdropFilter: "blur(2px)",
            WebkitBackdropFilter: "blur(2px)",
            opacity: isLoading ? 0.95 : 0,
            visibility: isLoading ? "visible" : "hidden",
            transition: "opacity 0.3s ease-in-out",
            pointerEvents: isLoading ? "auto" : "none",
          }}
        >
          <div className="d-flex flex-column align-items-center" style={{ gap: "1rem" }}>
            <div
              className="spinner-border"
              style={{ width: "3rem", height: "3rem" }}
            >
            </div>
            <div className="fs-3 fw-light">
              Please wait while your data is loading...
            </div>
          </div>
        </div>

        <div className="w-100 bg-light" style={{ flex: "1 1 auto", minHeight: 0, overflowY: "auto" }}>

          {/*        <div>
          <button className="btn btn-primary" onClick={toggleAnimation}>
            {isAnimating ? "Pause" : "Play"}
          </button>
        </div>*/}
          {volatilityAnalysisData &&
            currentDate &&
            volatilityAnalysisData[currentDate] ? (
            <>
                <div className="ms-4">
                <div>Current Date: {currentDate}</div>
                <div className="d-flex flex-row">
                  <label htmlFor="dateRange" className="form-label">
                    Select Date:
                  </label>
                    <div className="bg-light mx-2">
                    <input
                      type="range"
                      className="form-range"
                      id="dateRange"
                      name="dateRange"
                      value={formSlider}
                      min="0"
                      max={
                        Object.keys(volatilityAnalysisData).length
                          ? (Object.keys(volatilityAnalysisData).length - 1).toString()
                          : "3"
                      }
                      // max="20"
                      step="1"
                      onChange={updateSlider}
                    />
                  </div>
                  <div>Slider value: {formSlider}</div>
                </div>
                </div>
                <div className="d-flex justify-content-between flex-wrap mb-3">
                <Plot data={continuousFlatPricePlotData}
                  className="me-2"
                  layout={{
                    title: "Continuous Flat Price",
                    margin: { t: 50, b: 50, l: 75, r: 10 },
                    autosize: true,
                    xaxis: {
                      tickangle: 45
                    },
                    yaxis: {
                      tickformat: ",.2f",
                    },
                  }}
                />
                  <div className="bg-white d-inline-flex flex-row me-2">
                    <Plot
                      data={
                        [
                          ...Object.entries(selectedFutures)
                            .filter(([, meta]) => meta.checked)
                            .map(([future]) => {
                              const futureData = portalFuturesData[future];
                              if (!futureData || !futureData.historical_data) return null;

                              const dates = Object.keys(futureData.historical_data);
                              // sort dates ascending for Plotly ohlc time series
                              dates.sort();

                              return {
                                x: dates.map(date => new Date(date)),
                                // open: dates.map(date => futureData.historical_data[date]?.open),
                                // high: dates.map(date => futureData.historical_data[date]?.high),
                                // low: dates.map(date => futureData.historical_data[date]?.low),
                                y: dates.map(date => futureData.historical_data[date]?.close),
                                type: "scattergl",
                                mode: "lines+markers",
                                name: future,
                              };
                            })
                            .filter(Boolean),
                          {
                            type: "scattergl",
                          // xref: "x",
                          // yref: "paper",
                            x: [new Date(currentDate), new Date(currentDate)],
                            y: [
                              Math.min(
                                ...Object.keys(selectedFutures).flatMap((future) =>
                                  Object.keys(portalFuturesData[future].historical_data).map(
                                    (k) =>
                                      portalFuturesData[future].historical_data[k]?.low ??
                                      null,
                                  ),
                                ),
                              ),
                              Math.max(
                                ...Object.keys(selectedFutures).flatMap((future) =>
                                  Object.keys(portalFuturesData[future].historical_data).map(
                                    (k) =>
                                      portalFuturesData[future].historical_data[k]?.high ??
                                      null,
                                  ),
                                ),
                              ),
                            ],
                            // x0: currentDate,
                            // y0: 0,
                            // x1: currentDate,
                            // y1: 1,
                            line: { color: "blue", width: 4.0, dash: "solid" },
                            mode: "lines",
                            marker: { color: "blue" },
                            name: "Current Date",
                          }
                        ] as Object[]
                      }
                      // layout={{ width: 320, height: 240, title: "A Fancy Plot" }}
                      layout={{
                        title: "Flat Price over Option Range",

                        xaxis: {
                          fixedrange: false,
                          rangeslider: { visible: false },
                          range: [
                            new Date(startDate).getTime() - 1.5 * 24 * 60 * 60 * 1000,
                            new Date(endDate).getTime() + 0.5 * 24 * 60 * 60 * 1000,
                          ]
                        },
                        yaxis: {
                          range: [
                            (1 - 0.01) * //Assumes min fut is always positive, this is rarely untrue, use funct to simplify
                            Math.min(
                              ...Object.keys(portalFuturesData).filter((fut) => selectedFutures[fut].checked).flatMap(future =>
                                Object.keys(portalFuturesData[future].historical_data).filter((date: string) => new Date(date) >= startDate && new Date(date) <= endDate).flatMap(date => portalFuturesData[future].historical_data[date]?.low))
                              .filter(
                                (low) =>
                                  low !== null && typeof low === "number", // Is this null check needed if  fut filter used prev
                              ),
                            ),

                            (1 + 0.01) *
                            Math.max(
                              ...Object.keys(portalFuturesData).filter((fut) => selectedFutures[fut].checked).flatMap(future =>
                                Object.keys(portalFuturesData[future].historical_data).filter((date: string) => new Date(date) >= startDate && new Date(date) <= endDate).flatMap(date => portalFuturesData[future].historical_data[date]?.high))
                                .filter(
                                  (high) =>
                                    high !== null && typeof high === "number", // Is this null check needed if  fut filter used prev
                                ),
                            ),
                          ],


                        },
                      }}
                    />

                    {/* Futures Selection list */}
                    <div
                      className="card d-inline-block "
                      style={{
                        marginTop: "10px",
                        marginBottom: "10px",
                        marginRight: "15px",
                        width: "275px",
                        height: "430px",
                        verticalAlign: "top",
                      }}
                    >
                      <div
                        className="container h-100 p-2  d-flex flex-column"
                        style={{ overflowY: "auto" }}
                      >
                        <h6 className="text-center py-2">Available Futures</h6>
                        <div>
                          <label
                            className={`form-check-label ${styles.noSelect} ms-1`}
                            style={{ fontWeight: "bold" }}
                          >
                            <input
                              className="form-check-input"
                              type="checkbox"
                              value="all"
                              checked={Object.values(selectedFutures).every(
                                (item) => item.checked,
                              )}
                              onChange={handleFutSelectAll}
                            />
                            {" Select All"}
                          </label>
                          <br />
                        </div>
                        <hr className="my-0" />

                        <div className="flex-grow-1" style={{ overflowY: "auto" }}>
                          {(() => {
                            const sortedFutures = Object.entries(selectedFutures)
                              .sort(([a], [b]) => symbolSort(a, b));
                            const firstSelectedKey = sortedFutures.find(([, meta]) => meta.checked)?.[0];

                            return sortedFutures.map(([key, meta]) => (
                              <div
                                key={key}
                                ref={key === firstSelectedKey ? firstSelectedFuturesRef : null}
                              >
                                <label
                                  className={`form-check-label ${styles.noSelect} ms-1`}
                                >
                                  <input
                                    className="form-check-input"
                                    type="checkbox"
                                    value={key}
                                    checked={meta.checked}
                                    onChange={() => handleFutCheckboxChange(key)}
                                  />
                                  {" " +
                                    key +
                                    " ( " +
                                    Object.values(selectedOptions).filter(
                                      (item) => item.underlying === key,
                                    ).length +
                                    " ) - " +
                                    meta.expirationDate}
                                </label>
                                <br />
                              </div>
                            ));
                          })()}
                        </div>
                      </div>
                    </div>

                  </div>

                {/*
              [
                  {
                    x: Object.keys(volatilityAnalysisData[currentDate]).sort(
                      symbolSort,
                    ),
                    y: Object.keys(volatilityAnalysisData[currentDate])
                      .sort(symbolSort)
                      .map(
                        (k) =>
                          volatilityAnalysisData[currentDate][k]["close"],
                      ),
                    type: "scattergl",
                    mode: "lines+markers",
                    marker: { color: "blue" },
                  },

                 // { type: "bar", x: [1, 2, 3], y: [2, 5, 3] },
                ]

              */}

                <Plot
                  data={forwardCurvePlotData}
                  // layout={{ width: 320, height: 240, title: "A Fancy Plot" }}
                  layout={{
                    title: "Forward Curve",
                    yaxis: {
                      range: [
                        -5 +
                        Math.min(
                          ...Object.keys(volatilityAnalysisData).flatMap(
                            (date) =>
                              Object.keys(volatilityAnalysisData[date])
                                .map(
                                  (future) =>
                                    volatilityAnalysisData[date][future]
                                      .close,
                                )
                                .filter(
                                  (close) =>
                                    close !== null &&
                                    typeof close === "number",
                                ),
                          ),
                        ),
                        5 +
                        Math.max(
                          ...Object.keys(volatilityAnalysisData).flatMap(
                            (date) =>
                              Object.keys(volatilityAnalysisData[date]).map(
                                (future) =>
                                  volatilityAnalysisData[date][future].close,
                              ),
                          ),
                        ),
                      ],
                    },
                  }}
                />
              </div>
                <div className="d-flex flex-row justify-content-between flex-wrap">
                <Plot
                    style={{ height: "575px", width: "700px" }}
                  data={Object.entries(selectedOptions)
                    .filter(([, meta]) => meta.checked) // && meta.visible
                    .map(([option, meta]) => {
                      const underlyingData =
                        volatilityAnalysisData[currentDate]?.[meta.underlying];
                      const optionData = underlyingData?.options?.[option];

                      if (optionData && underlyingData) {
                        const futureClose = underlyingData["close"];

                        const ivolPuts = Object.entries(
                          optionData.ivol?.Puts ?? {},
                        ).filter(([strike]) => Number(strike) < futureClose);
                        const ivolCalls = Object.entries(
                          optionData.ivol?.Calls ?? {},
                        ).filter(([strike]) => Number(strike) >= futureClose);

                        const ivolSorted = [...ivolPuts, ...ivolCalls].sort(
                          ([strikeA], [strikeB]) =>
                            Number(strikeA) - Number(strikeB),
                        );

                        return {
                          x: ivolSorted.map(([strike]) => Number(strike)),
                          y: ivolSorted.map(([, ivol]) => ivol),
                          type: "scattergl",
                          mode: "lines+markers",
                          name: option,
                          showlegend: true,
                        };
                      } else {
                        return {
                          x: [NaN],
                          y: [NaN],
                          type: "scattergl",
                          mode: "lines+markers",
                          name: option,
                          showlegend: true,
                        };
                      }
                    })}
                  // layout={{ width: 320, height: 240, title: "A Fancy Plot" }}
                    layout={{
                      ...layout,
                      yaxis: {
                        range: [
                          0.01,
                          0.3
                        ]
                      },
                      xaxis: {
                        range: ivolChartRange
                      },
                      uirevision: ivolUiRev

                    }}
                    onRelayout={(event) => handleRelayout(event)}
                  />


                {/* option table */}
                  <div className="" style={{ width: "1050px", height: "575px" }}>
                    <div className="card h-100">
                    <div className="card-header d-flex justify-content-between">
                      <span className="fw-semibold mt-1">Option Selection</span>
                      <div className="d-flex gap-2">
                        <button disabled>Select All</button>
                        <button
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            Object.keys(selectedFutures).length == expandedFutures.length
                              ? setExpandedFutures([])
                              : setExpandedFutures(
                                Object.keys(selectedFutures).map(
                                  (future) => future,
                                ),
                              );
                          }}
                          style={{ width: 100, padding: "0px 5px" }}
                        >
                          {Object.keys(selectedFutures).length == expandedFutures.length
                            ? "Collapse All"
                            : "Expand All"}
                        </button>
                      </div>
                    </div>
                    <div
                      className="card-body overflow-y-scroll pt-0 px-0"
                      style={{ width: "100%", height: "300px" }}
                    >
                      <table
                        className="table table-hover table-sm mb-0"
                        style={{ tableLayout: "fixed", width: "100%" }}
                      >
                        <thead className="sticky-top">
                          <tr>
                            <th style={{ width: "5%" }}></th>
                            <th style={{ width: "20%" }}>Symbol</th>
                            <th style={{ width: "15%" }}>Expiration Date</th>
                            <th style={{ width: "25%" }}>Description </th>
                            <th style={{ width: "10%" }}>Month</th>
                            <th style={{ width: "5%" }}>Year</th>
                            <th style={{ width: "20%", textAlign: "center" }}>
                              Action
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                            {Object.entries(selectedFutures).sort(([a], [b]) => symbolSort(a, b)).map(([future, futMeta], idx) => {
                              const itemKey = future;
                            if (Object.entries(selectedOptions).filter(([, meta]) => meta.underlying === future).length == 0) {
                              return null;
                            }
                            return (
                              <React.Fragment key={itemKey}>
                                <tr
                                  className="table-secondary "
                                  onClick={() => toggleExpandedFuture(itemKey)}
                                >
                                  <td className="">
                                    <div className="d-flex justify-content-center">
                                      <i
                                        className={`bi bi-chevron-right ${styles.chevronIcon
                                          } ${expandedFutures.includes(itemKey)
                                            ? styles.expanded
                                            : styles.collapsed
                                          }`}
                                      ></i>
                                    </div>
                                  </td>
                                  <td>
                                    <b>{future}</b>
                                  </td>
                                  <td>
                                    <b>{futMeta.expirationDate}</b>
                                  </td>
                                  <td style={{}}>
                                    <b>{futMeta.name || "unavailable"}</b>
                                  </td>
                                  <td
                                    style={{
                                      verticalAlign: "middle",
                                      textAlign: "center",
                                    }}
                                  >
                                    <b>
                                      {new Date(
                                        0,
                                        (calcSymbolMonthYear(future)
                                          .month_number ?? 1) - 1,
                                      ).toLocaleString("en-US", {
                                        month: "short",
                                      })}
                                    </b>
                                  </td>
                                  <td
                                    style={{
                                      verticalAlign: "middle",
                                      textAlign: "center",
                                    }}
                                  >
                                    <b>
                                      {calcSymbolMonthYear(future).year_4digit}
                                    </b>
                                  </td>
                                  <td className="text-center">
                                    <button
                                      className="px-3 rounded-1"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        Object.entries(selectedOptions)
                                          .filter(
                                            ([option, meta]) =>
                                              meta.underlying === future,
                                          )
                                          .map(([option, meta]) => {
                                            if (
                                              !selectedOptions[option].checked
                                            ) {
                                              handleOptCheckboxChange(option);
                                            }
                                          });
                                      }}
                                    >
                                      Select All
                                    </button>
                                  </td>
                                </tr>
                                {expandedFutures.includes(itemKey) && (
                                  <tr>
                                    <td colSpan={7} className="p-0">
                                      <div className={styles.nestedTable}>
                                        <table className="table table-sm mb-0">
                                          <tbody>
                                            {Object.entries(selectedOptions)
                                              .filter(([, optMeta]) => optMeta.underlying === future)
                                              .filter(([option, optMeta]) => selectedOptionDescriptionFilters.some(filter => optMeta.name?.includes(filter)) || !optionDescriptionFilters.some(filter => optMeta.name?.includes(filter)))
                                              .sort(([a], [b]) => symbolSort(a, b))
                                              .map(([option, optMeta]) => (
                                                <tr
                                                  key={option}
                                                  onClick={() =>
                                                    handleOptCheckboxChange(
                                                      option,
                                                    )
                                                  }
                                                  style={{ cursor: "pointer" }}
                                                >
                                                  <td
                                                    className="text-center"
                                                    style={{ width: "5%" }}
                                                  >
                                                    <input
                                                      type="checkbox"
                                                      id={option}
                                                      className="form-check-input"
                                                      checked={
                                                        selectedOptions[option]
                                                          ?.checked || false
                                                      }
                                                      onChange={() => { }} // Handled by row click
                                                    />
                                                  </td>
                                                  <td style={{ width: "20%" }}>
                                                    <div className="d-flex justify-content-end pe-3">
                                                      {option}
                                                    </div>
                                                  </td>
                                                  <td style={{ width: "15%" }}>
                                                    <div className="d-flex justify-content-end pe-3">
                                                      {optMeta.expirationDate}
                                                    </div>
                                                  </td>
                                                  <td
                                                    style={{
                                                      width: "25%",
                                                      fontSize: "10px",
                                                      textAlign: "center",
                                                      verticalAlign: "middle",
                                                    }}
                                                  >
                                                    <div className="">
                                                      {optMeta.name ||
                                                        (option.slice(0, -3) ===
                                                          future.slice(0, -3) &&
                                                          futMeta.name) ||
                                                        "unavailable"}
                                                    </div>
                                                  </td>
                                                  <td
                                                    style={{
                                                      width: "10%",
                                                      fontSize: "8px",
                                                    }}
                                                  >
                                                    <div className="d-flex justify-content-end pe-3 ">
                                                      {
                                                        "coming soon" /*option.month*/
                                                      }
                                                    </div>
                                                  </td>
                                                  <td
                                                    style={{
                                                      width: "5%",
                                                      fontSize: "8px",
                                                    }}
                                                  >
                                                    <div className="d-flex justify-content-end pe-3">
                                                      {
                                                        "coming soon" /*option.year*/
                                                      }
                                                    </div>
                                                  </td>
                                                  <td
                                                    style={{ width: "20%" }}
                                                  ></td>
                                                </tr>
                                              ))}
                                          </tbody>
                                        </table>
                                      </div>
                                    </td>
                                  </tr>
                                )}
                              </React.Fragment>
                            );
                          })}
                          </tbody>
                        </table>
                      </div>
                      <div className="card-footer">
                        <span className="d-flex gap-2">
                          {optionDescriptionFilters.filter((filter) => Object.values(selectedOptions).some((option) => option.name?.includes(filter))).map((filter) => (
                            <div key={filter} className="form-check">
                              <input id={filter} type="checkbox" className="form-check-input" checked={selectedOptionDescriptionFilters.includes(filter)} onChange={() => setSelectedOptionDescriptionFilters(prev => prev.includes(filter) ? prev.filter(f => f !== filter) : [...prev, filter])} />
                              <label className="form-check-label" htmlFor={filter}>
                                {filter}
                              </label>
                            </div>
                          ))}
                        </span>
                      </div>
                    </div>
                  </div>
                  {/* End of option table */}

                  <Plot
                    data={ivolForwardCurvePlotData.plotData}
                    layout={{
                      title: "IVolatility Forward Curve",
                      yaxis: {
                        range: ivolCurveChartRange
                      },
                      xaxis: {
                        showticklabels: false,
                      },
                      annotations: ivolForwardCurvePlotData.annotations
                    }}
                  />

                </div>
              </>
          ) : (
            <div className="p-5 w-100 d-flex flex-column align-items-center justify-content-center">
              <div className="alert alert-info text-center" role="alert">
                  <p>{hasData ? "Please wait while data is loading..." : "No data available to display."}</p>

                  {hasData ? <div className="spinner-border" style={{ width: "2rem", height: "2rem" }}></div> : <i className="bi bi-bar-chart-line-fill fs-2 mb-2 d-block"></i>}
                <p>Enter a symbol and select a date range to view data.</p>
              </div>
            </div>
          )}
        </div>
        {/* <footer className="bg-info-subtle w-100 py-2 px-5" style={{ flexShrink: 0 }}>
          <div className="d-flex flex-row justify-content-center align-items-center gap-2">

            <div className="position-relative d-inline-flex align-items-center justify-content-center">
              <div
                className="position-absolute"
                style={{
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  width: '25px',
                  height: '25px',
                  backgroundColor: 'black',
                  clipPath: 'polygon(50% 0%, 0% 100%, 100% 100%)',
                  zIndex: 1
                }}
              ></div>
              <i className="bi bi-exclamation-triangle-fill text-warning fs-2 position-relative" style={{ zIndex: 2 }}>
              </i>
            </div>
            <span className="fw-semibold fs-5 ">Experimental Updates: some requests may be unresponsive or require the page to be refreshed</span>

          </div>
        </footer> */}
      </div>
    </div>
  );
}

export default VolatilityAnalysis;
